import React, { useCallback, useEffect, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  Eye,
  MoreVertical,
  Pencil,
  Plus,
  Receipt,
  Search,
  SlidersHorizontal,
  Upload,
  X,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import DashboardLayout from "src/components/layout/dashboard/Layout";
import TableV2 from "src/components/ui/TableV2";
import CardHeader from "src/components/common/card/CardHeader";
import Button from "src/components/ui/Button";
import Input from "src/components/ui/Input";
import FilterSelect from "src/components/ui/FilterSelect";
import PageSizeSelect from "src/components/ui/PageSizeSelect";
import Card from "src/components/common/card/Card";
import Modal from "src/components/ui/Modal";
import Popover from "src/components/ui/Popover";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as salesActions from "src/redux/slices/salesSlice";
import * as customerActions from "src/redux/slices/customerSlice";
import { formatDateTime } from "src/utils/date-time";
import { currencyFormatter } from "src/utils/common";
import usePlanUsage from "src/hooks/usePlanUsage";
import { Badge } from "src/components/ui/Badge";
import { toast } from "react-toastify";

const ORDER_STATUS_OPTIONS = [
  { label: "Pending", value: "pending" },
  { label: "Completed", value: "completed" },
  { label: "Cancelled", value: "cancelled" },
];

const PAYMENT_STATUS_OPTIONS = [
  { label: "Paid", value: "paid" },
  { label: "Pending", value: "pending" },
  { label: "Cancelled", value: "cancelled" },
];

const orderStatusColor = (status: string) =>
  status === "completed" ? "green" : status === "cancelled" ? "red" : "yellow";

const capitalize = (text: string) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : text;

const SalesListPage: React.FC = () => {
  const [limit, setLimit] = useState(10);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [orderStatus, setOrderStatus] = useState<
    "" | "pending" | "completed" | "cancelled"
  >("");
  const [paymentStatus, setPaymentStatus] = useState<
    "" | "paid" | "pending" | "cancelled"
  >("");
  const [customerId, setCustomerId] = useState("");

  const { salesList } = useAppSelector((state) => state.sales);
  const { list: customerList } = useAppSelector((state) => state.customer);
  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);
  const permissions = user?.roleDetails?.permissions || user?.permissions || {};
  const isTenantAdmin =
    user?.role === "admin" ||
    user?.roleDetails?.name?.toLowerCase() === "admin";
  const canCreateSales =
    isTenantAdmin || (permissions?.sales || []).includes("create");
  const canEditSales =
    isTenantAdmin || (permissions?.sales || []).includes("edit");
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { planUsage } = usePlanUsage();

  const saleLimit = planUsage?.plan?.features?.totalSaleOrders;
  const salesUsage = planUsage?.usage?.totalSaleOrders;
  const isPlanExpired = planUsage?.planStatus === "expired";
  const isSaleLimitReached =
    typeof saleLimit === "number" &&
    typeof salesUsage === "number" &&
    salesUsage >= saleLimit;
  const disableCreateSale = isPlanExpired || isSaleLimitReached;

  const isRowOwner = (row: any) => {
    const createdById =
      typeof row?.createdBy === "string" ? row.createdBy : row?.createdBy?._id;
    if (!createdById || !user?._id) return false;
    return String(createdById) === String(user._id);
  };

  const getSalesOrders = useCallback(
    (page: number) => {
      dispatch(
        salesActions.getSalesListRequest({
          page,
          limit,
          ...(search ? { search } : {}),
          ...(orderStatus ? { orderStatus } : {}),
          ...(paymentStatus ? { paymentStatus } : {}),
          ...(customerId ? { customerId } : {}),
        })
      );
    },
    [dispatch, limit, search, orderStatus, paymentStatus, customerId]
  );

  useEffect(() => {
    dispatch(customerActions.getCustomersListRequest({ page: 1, limit: 100 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce the search box before it hits the API/filters
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    getSalesOrders(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, orderStatus, paymentStatus, customerId, limit]);

  const handlePageChange = (page: number) => getSalesOrders(page);

  const handleClearFilters = () => {
    setSearchInput("");
    setSearch("");
    setOrderStatus("");
    setPaymentStatus("");
    setCustomerId("");
  };
  const hasActiveFilters = !!(search || orderStatus || paymentStatus || customerId);

  return (
    <DashboardLayout
      meta={{
        title: "Sales Orders | Invyntra Dashboard",
        description:
          "View and manage all sales orders with customer and invoice details.",
        keywords: "sales orders, invoices, customers, invyntra",
      }}
    >
      <div className="space-y-4">
        {/* Page header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary-100 text-primary-600 dark:bg-primary-900/40 dark:text-primary-300">
              <Receipt size={26} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                Sales Orders
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                View and manage all sales orders.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {canCreateSales && (
              <Button
                variant="outline"
                rounded="full"
                onClick={() => setShowImportModal(true)}
              >
                <Upload size={16} className="mr-1.5" />
                Bulk import
              </Button>
            )}
            {canCreateSales && (
              <Button
                rounded="full"
                onClick={() => navigate("/app/sale/create")}
                disabled={disableCreateSale}
                title={
                  isPlanExpired
                    ? "Plan expired. Please upgrade."
                    : isSaleLimitReached
                    ? "Order limit reached. Please upgrade."
                    : undefined
                }
              >
                <Plus size={16} className="mr-1" />
                <span>{disableCreateSale ? "Limit Reached" : "Create Sale"}</span>
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <Card>
          <div className="flex w-full items-center justify-between">
            <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <SlidersHorizontal size={14} />
              Filters
              {hasActiveFilters && (
                <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />
              )}
            </span>
            <Button
              variant="outline"
              size="sm"
              rounded="full"
              onClick={() => setFiltersOpen((o) => !o)}
            >
              {filtersOpen ? "Hide filters" : "Show filters"}
              {filtersOpen ? (
                <ChevronUp size={16} className="ml-1.5" />
              ) : (
                <ChevronDown size={16} className="ml-1.5" />
              )}
            </Button>
          </div>

          {filtersOpen && (
            <div className="mt-4 grid grid-cols-1 gap-4 border-t border-gray-100 pt-4 dark:border-gray-700 sm:grid-cols-2 lg:grid-cols-5">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Search
                </label>
                <Input
                  placeholder="Search order id..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  startAdornment={<Search size={14} />}
                />
              </div>

              <FilterSelect
                variant="boxed"
                label="Customer"
                value={customerId}
                onChange={setCustomerId}
                allLabel="All customers"
                options={(customerList?.data?.data || []).map((c: any) => ({
                  label: c.name,
                  value: c._id,
                }))}
              />

              <FilterSelect
                variant="boxed"
                label="Order Status"
                value={orderStatus}
                onChange={(val) => setOrderStatus(val as typeof orderStatus)}
                allLabel="All statuses"
                options={ORDER_STATUS_OPTIONS}
              />

              <FilterSelect
                variant="boxed"
                label="Payment Status"
                value={paymentStatus}
                onChange={(val) => setPaymentStatus(val as typeof paymentStatus)}
                allLabel="All statuses"
                options={PAYMENT_STATUS_OPTIONS}
              />

              <div className="flex items-end">
                <Button
                  variant="outline"
                  color="danger"
                  rounded="full"
                  fullWidth
                  onClick={handleClearFilters}
                  disabled={!hasActiveFilters}
                >
                  <X size={15} className="mr-1.5" />
                  Clear filters
                </Button>
              </div>
            </div>
          )}
        </Card>

        {/* List */}
        <Card>
          <CardHeader
            title="Sales orders"
            description="Search and manage your sales orders."
            actions={<PageSizeSelect value={limit} onChange={setLimit} />}
          />

          <TableV2
            columns={[
              {
                key: "orderId",
                header: "Order ID",
                accessor: ({ row }) => (
                  <Link
                    to={`/app/sale/${row._id}`}
                    className="font-medium text-primary-600 hover:underline dark:text-primary-400"
                  >
                    #{row.orderId}
                  </Link>
                ),
              },
              {
                key: "customerId.name",
                header: "Customer",
                accessor: ({ row }) => row.customerId?.name || "—",
              },
              {
                key: "grandTotal",
                header: "Total Amount",
                align: "right",
                accessor: ({ row }) => (
                  <span className="font-semibold text-primary-700 dark:text-primary-400">
                    {currencyFormatter(row.grandTotal)}
                  </span>
                ),
              },
              {
                key: "paymentMode",
                header: "Payment Mode",
                accessor: ({ row }) => row.paymentMode?.toUpperCase() || "-",
              },
              {
                key: "status",
                header: "Status",
                accessor: ({ row }) =>
                  row.orderStatus ? (
                    <Badge
                      color={orderStatusColor(row.orderStatus)}
                      text={capitalize(row.orderStatus)}
                    />
                  ) : (
                    "—"
                  ),
              },
              {
                key: "paymentStatus",
                header: "Payment Status",
                accessor: ({ row }) =>
                  row.orderStatus !== "cancelled" ? (
                    <Badge
                      color={row.paymentStatus === "paid" ? "green" : "yellow"}
                      text={capitalize(row.paymentStatus)}
                    />
                  ) : (
                    "—"
                  ),
              },
              {
                key: "orderDate",
                header: "Date",
                accessor: ({ row }) =>
                  formatDateTime(row.orderDate || row.createdAt, "DD MMM YYYY"),
              },
              {
                key: "actions",
                header: "Action",
                accessor: ({ row }) => (
                  <Popover
                    trigger={
                      <button
                        className="p-2 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100"
                        title="Actions"
                      >
                        <MoreVertical size={16} />
                      </button>
                    }
                  >
                    {(close) => (
                      <div className="w-44 py-1.5">
                        <button
                          className="flex w-full items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                          onClick={() => {
                            close();
                            navigate(`/app/sale/${row._id}`);
                          }}
                        >
                          <Eye size={14} />
                          View
                        </button>
                        {canEditSales &&
                          row.orderStatus === "pending" &&
                          (isTenantAdmin || isRowOwner(row)) && (
                            <button
                              className="flex w-full items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                              onClick={() => {
                                close();
                                navigate(`/app/sale/${row._id}/edit`);
                              }}
                            >
                              <Pencil size={14} />
                              Edit
                            </button>
                          )}
                      </div>
                    )}
                  </Popover>
                ),
              },
            ]}
            data={salesList?.data?.data}
            pagination={salesList?.data?.pagination}
            onPageChange={handlePageChange}
            loading={salesList.loading}
          />
        </Card>
      </div>

      {showImportModal && (
        <Modal
          open={showImportModal}
          onClose={() => setShowImportModal(false)}
          title="Bulk import sales orders"
        >
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Download a sample file to see columns (customer, products, quantities,
            prices). First row must be headers.
          </p>
          <div className="mt-4 flex gap-3">
            <Button
              variant="outline"
              onClick={() => toast.info("Sample file download is coming soon.")}
            >
              Sample Excel
            </Button>
            <Button
              onClick={() =>
                toast.info("Bulk import isn't wired up yet — coming soon.")
              }
            >
              Upload &amp; import
            </Button>
          </div>
        </Modal>
      )}
    </DashboardLayout>
  );
};

export default SalesListPage;
